import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Observable, Subscription, forkJoin } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { LeadActivityService } from 'src/app/private/services/lead-activity.service';
import { LeadService } from 'src/app/private/services/lead.service';
import { PipeLineService } from 'src/app/private/services/pipeline.service';
import { UsersService } from 'src/app/private/services/users.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { normalizeFieldOptions } from 'src/app/utils/lead-field-options.util';
import { normalizeLeadEditModel } from 'src/app/utils/lead-profile-field.util';
import {
  applyCrmSourcePipeline,
  findPipelineById,
  isCrmLeadSource,
  pipelineStageLabel,
} from 'src/app/utils/crm-lead-pipeline.util';
import { MODULE_LOADING_MESSAGES } from 'src/app/private/services/module.config';

import { Customer360Service } from 'src/app/private/services/customer-360.service';
import { LeadProfileActivityPanelComponent } from 'src/app/private/modules/components/leads/lead-profile-activity-panel/lead-profile-activity-panel.component';
export type LeadProfileMainTab =
  | 'overview'
  | 'activities'
  | 'transactions'
  | 'loyalty'
  | 'tickets'
  | 'support'
  | 'feedback'
  | 'documents'
  | 'analytics';
export type LeadActivitySubTab =
  | 'activity'
  | 'note'
  | 'email'
  | 'whatsapp'
  | 'task'
  | 'meeting'
  | 'call'
  | 'followups';

@Component({
  selector: 'app-lead-profile',
  templateUrl: './lead-profile.component.html',
  styleUrls: ['./lead-profile.component.scss'],
})
export class LeadProfileComponent implements OnInit, OnDestroy {
  @Input() editFieldValues: any;
  @Input() syncTabsToRoute = false;
  @Output() close = new EventEmitter<void>();
  @Output() reloadLeadList = new EventEmitter<void>();

  activeTab: LeadProfileMainTab = 'overview';
  activitySubTab: LeadActivitySubTab = 'activity';
  timelineRefresh = 0;

  readonly quickActionButtons: {
    id: LeadActivitySubTab;
    label: string;
    iconClasses: string;
  }[] = [
    { id: 'note', label: 'Notes', iconClasses: 'bi bi-journal-text' },
    { id: 'email', label: 'Email', iconClasses: 'fas fa-envelope' },
    { id: 'whatsapp', label: 'WhatsApp', iconClasses: 'fab fa-whatsapp' },
    { id: 'task', label: 'Task', iconClasses: 'fas fa-tasks' },
    { id: 'meeting', label: 'Meeting', iconClasses: 'fas fa-calendar' },
    { id: 'call', label: 'Call', iconClasses: 'fas fa-phone' },
  ];

  readonly activitySubTabs: { id: LeadActivitySubTab; label: string }[] = [
    { id: 'activity', label: 'Activity' },
    { id: 'note', label: 'Note' },
    { id: 'email', label: 'Email' },
    { id: 'whatsapp', label: 'WhatsApp' },
    { id: 'task', label: 'Task' },
    { id: 'meeting', label: 'Meeting' },
    { id: 'call', label: 'Call' },
    { id: 'followups', label: 'Follow Ups' },
  ];

  pipelineData: any[] = [];
  leadStatusList: { value: string; label: string }[] = [];
  users: any[] = [];
  allLeadFields: any[] = [];
  fieldsLoading = false;
  btnSaveSidebar = false;

  // Custom fields loader rotation fields
  fieldsActiveMessages: string[] = [];
  fieldsCurrentMessageIndex = 0;
  fieldsCurrentMessage = '';
  fieldsFadeState: 'fade-out' | 'fade-in' | '' = '';
  private fieldsMessageInterval: any = null;
  preUserId: any;
  preLeadStatus = '';
  prePipelineId: any;
  preLeadType = '';
  preLeadSource = '';
  customerRefresh = 0;
  private queryParamSub?: Subscription;
  @ViewChild('activityPanel') activityPanel?: LeadProfileActivityPanelComponent;

  private readonly validMainTabs = new Set<LeadProfileMainTab>([
    'overview',
    'activities',
    'transactions',
    'loyalty',
    'tickets',
    'support',
    'feedback',
    'documents',
    'analytics',
  ]);

  private readonly validActivityTabs = new Set<LeadActivitySubTab>([
    'activity',
    'note',
    'email',
    'whatsapp',
    'task',
    'meeting',
    'call',
    'followups',
  ]);

  constructor(
    private leadApi: LeadService,
    private pipelineApi: PipeLineService,
    private usersApi: UsersService,
    private leadActivity: LeadActivityService,
    private customer360: Customer360Service,
    private toast: ToastrService,
    private localStorage: LocalStorageService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (this.syncTabsToRoute) {
      this.applyTabsFromRoute(this.route.snapshot.queryParamMap);
      this.queryParamSub = this.route.queryParamMap.subscribe((query) =>
        this.applyTabsFromRoute(query)
      );
    }

    if (!this.editFieldValues?.fields) {
      this.editFieldValues.fields = {};
    }
    normalizeLeadEditModel(this.editFieldValues, this.allLeadFields);
    this.snapshotSidebarBaselines();
    this.loadPipelines();
    this.loadUsers();
    this.loadLeadFields();
    this.loadCustomerProfile();
    this.refreshTimeline(true);
  }

  ngOnDestroy(): void {
    this.queryParamSub?.unsubscribe();
    this.cleanupFieldsMessageRotation();
  }

  private applyTabsFromRoute(query: ParamMap): void {
    const tab = query.get('tab');
    const activityTab = query.get('activityTab') as LeadActivitySubTab | null;

    if (tab && this.validMainTabs.has(tab as LeadProfileMainTab)) {
      this.activeTab = tab as LeadProfileMainTab;
    } else if (tab === 'transaction-history' || tab === 'behavioral-patterns') {
      this.activeTab = 'analytics';
    }
    if (activityTab && this.validActivityTabs.has(activityTab)) {
      this.activitySubTab = activityTab;
    }
  }

  private updateRouteTabs(): void {
    if (!this.syncTabsToRoute) {
      return;
    }

    const queryParams: Record<string, string | null> = {
      tab: this.activeTab === 'overview' ? null : this.activeTab,
      activityTab:
        this.activeTab === 'activities' && this.activitySubTab !== 'activity'
          ? this.activitySubTab
          : null,
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  loadCustomerProfile(): void {
    if (this.leadId == null || this.leadId === '') {
      return;
    }
    this.customer360.getCustomerProfile(this.leadId).subscribe({
      next: (profile: any) => {
        if (!profile || !this.editFieldValues) {
          return;
        }
        if (profile.customer_id != null) {
          this.editFieldValues.customer_id = profile.customer_id;
        }
        if (profile.lead_source != null) {
          this.editFieldValues.lead_source = profile.lead_source;
        }
        if (profile.is_channel_customer != null) {
          this.editFieldValues.is_channel_customer = profile.is_channel_customer;
        }
        this.customerRefresh++;
      },
    });
  }

  loadLeadFields(): void {
    this.fieldsLoading = true;
    this.startFieldsMessageRotation();
    this.leadApi.getLeadsFields({ client_id: this.localStorage.getClientId() }).subscribe({
      next: (data: any) => {
        const raw = data?.fields;
        this.allLeadFields = Array.isArray(raw)
          ? [...raw]
              .sort((a: any, b: any) => (a.sequence ?? 0) - (b.sequence ?? 0))
              .map((f: any) => ({
                ...f,
                data: normalizeFieldOptions(f.data),
                sub_fields: Array.isArray(f.sub_fields)
                  ? f.sub_fields.map((sf: any) => ({
                      ...sf,
                      data: normalizeFieldOptions(sf.data),
                    }))
                  : f.sub_fields,
              }))
          : [];
        this.fieldsLoading = false;
        this.cleanupFieldsMessageRotation();
        normalizeLeadEditModel(this.editFieldValues, this.allLeadFields);
      },
      error: () => {
        this.allLeadFields = [];
        this.fieldsLoading = false;
        this.cleanupFieldsMessageRotation();
      },
    });
  }

  private startFieldsMessageRotation() {
    if (this.fieldsMessageInterval) return; // Prevent multiple intervals
    this.fieldsActiveMessages = MODULE_LOADING_MESSAGES['configuration-leads'] || MODULE_LOADING_MESSAGES['leads'] || [];
    this.fieldsCurrentMessageIndex = 0;
    this.fieldsCurrentMessage = this.fieldsActiveMessages[this.fieldsCurrentMessageIndex] || 'Loading...';
    this.fieldsFadeState = '';

    this.fieldsMessageInterval = setInterval(() => {
      this.fieldsFadeState = 'fade-out';
      setTimeout(() => {
        if (!this.fieldsLoading) {
          this.cleanupFieldsMessageRotation();
          return;
        }
        this.fieldsCurrentMessageIndex = (this.fieldsCurrentMessageIndex + 1) % this.fieldsActiveMessages.length;
        this.fieldsCurrentMessage = this.fieldsActiveMessages[this.fieldsCurrentMessageIndex];
        this.fieldsFadeState = 'fade-in';
        setTimeout(() => {
          this.fieldsFadeState = '';
        }, 200);
      }, 200);
    }, 2000);
  }

  private cleanupFieldsMessageRotation() {
    if (this.fieldsMessageInterval) {
      clearInterval(this.fieldsMessageInterval);
      this.fieldsMessageInterval = null;
    }
  }

  get leadId(): number | string {
    return this.editFieldValues?.lead_id;
  }

  get pipelineStages(): { stage_name: string; probability?: number }[] {
    const pipe = findPipelineById(this.pipelineData, this.editFieldValues?.pipeline_id);
    return pipe?.stages ?? [];
  }

  get displayName(): string {
    return (
      this.editFieldValues?.contact_name ||
      [this.editFieldValues?.fields?.first_name, this.editFieldValues?.fields?.last_name]
        .filter(Boolean)
        .join(' ') ||
      'Lead'
    );
  }

  get displayEmail(): string {
    return this.editFieldValues?.contact_email || this.editFieldValues?.fields?.email || '—';
  }

  get displayPhone(): string | null {
    return (
      this.editFieldValues?.contact_phone ||
      this.editFieldValues?.fields?.phone ||
      this.editFieldValues?.fields?.mobile ||
      null
    );
  }

  get initials(): string {
    const parts = this.displayName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return (parts[0]?.[0] || 'L').toUpperCase();
  }

  setMainTab(tab: LeadProfileMainTab): void {
    this.activeTab = tab;
    if (tab === 'activities') {
      this.refreshTimeline(false);
    }
    if (
      tab === 'transactions' ||
      tab === 'loyalty' ||
      tab === 'tickets' ||
      tab === 'support' ||
      tab === 'feedback' ||
      tab === 'documents' ||
      tab === 'analytics'
    ) {
      this.customerRefresh++;
    }
    this.updateRouteTabs();
  }

  onCustomerDataChanged(): void {
    this.customerRefresh++;
    this.refreshTimelineAfterLog();
  }

  setActivitySubTab(tab: LeadActivitySubTab): void {
    this.activitySubTab = tab;
    this.updateRouteTabs();
  }

  openQuickAction(tab: LeadActivitySubTab): void {
    this.setMainTab('activities');
    this.setActivitySubTab(tab);
    if (tab === 'email' || tab === 'whatsapp' || tab === 'call' || tab === 'meeting') {
      setTimeout(() => this.activityPanel?.onAdd(), 0);
    }
  }

  loadPipelines(): void {
    this.pipelineApi
      .getAllPipeLine({
        client_id: this.localStorage.getClientId(),
        pipeline_type: 'lead',
      })
      .subscribe({
        next: (res: any) => {
          this.pipelineData = res || [];
          const appliedCrmPipeline = applyCrmSourcePipeline(
            this.editFieldValues,
            this.pipelineData
          );
          if (!appliedCrmPipeline && !this.editFieldValues.pipeline_id && this.pipelineData.length) {
            this.editFieldValues.pipeline_id = this.pipelineData[0].pipeline_id;
          }
          this.getLeadStages(this.editFieldValues.pipeline_id);
        },
      });
  }

  loadUsers(): void {
    this.usersApi.findAllUsers({ client_id: this.localStorage.getClientId() }).subscribe({
      next: (res: any) => {
        this.users = Array.isArray(res) ? res : res?.rows ?? [];
      },
    });
  }

  getLeadStages(pipelineId: any): void {
    if (!pipelineId) {
      this.leadStatusList = [];
      return;
    }
    const pipe = findPipelineById(this.pipelineData, pipelineId);
    this.leadStatusList =
      pipe?.stages?.map((stage: any) => ({
        value: stage.stage_name,
        label: pipelineStageLabel(stage),
      })) ?? [];
    normalizeLeadEditModel(this.editFieldValues, this.allLeadFields);
  }

  onSourceChange(): void {
    if (applyCrmSourcePipeline(this.editFieldValues, this.pipelineData)) {
      this.getLeadStages(this.editFieldValues.pipeline_id);
    }
  }

  isCrmImportedSource(source: unknown): boolean {
    return isCrmLeadSource(source);
  }

  determineLeadType(status: string): string | null {
    if (!status || !this.pipelineData || !this.editFieldValues?.pipeline_id) return null;
    const pipe = findPipelineById(this.pipelineData, this.editFieldValues.pipeline_id);
    if (!pipe || !pipe.stages) return null;
    const stage = pipe.stages.find((s: any) => (s.stage_name || '').trim().toLowerCase() === status.trim().toLowerCase());
    if (!stage || stage.probability == null) return null;
    const prob = Number(stage.probability);
    if (isNaN(prob)) return null;
    if (prob < 30) return 'Cold Lead';
    if (prob < 70) return 'Warm Lead';
    return 'Hot Lead';
  }

  onSidebarStatusChange(newStatus: string): void {
    const autoType = this.determineLeadType(newStatus);
    if (autoType) {
      this.editFieldValues.lead_type = autoType;
    }
  }

  onPipelineChange(): void {
    this.getLeadStages(this.editFieldValues.pipeline_id);
    const autoType = this.determineLeadType(this.editFieldValues.lead_status);
    if (autoType) {
      this.editFieldValues.lead_type = autoType;
    }
  }

  refreshTimeline(runBackfill = false): void {
    const bump = () => {
      this.timelineRefresh++;
    };
    if (runBackfill) {
      this.leadActivity.backfillLeadActivity(this.leadId).subscribe({
        next: () => bump(),
        error: () => bump(),
      });
    } else {
      bump();
    }
  }

  /** Refresh timeline after optional activity log write completes (avoids stale fetch). */
  private refreshTimelineAfterLog(log$?: Observable<unknown>): void {
    const bump = () => {
      this.timelineRefresh++;
    };
    if (log$) {
      log$.subscribe({ next: bump, error: bump });
    } else {
      bump();
    }
  }

  onActivityRecorded(): void {
    this.refreshTimelineAfterLog();
    this.reloadLeadList.emit();
  }

  onFollowUpSaved(): void {
    this.onActivityRecorded();
  }

  onOverviewSaved(): void {
    this.reloadLeadList.emit();
    this.refreshTimelineAfterLog();
  }

  private snapshotSidebarBaselines(): void {
    this.preUserId = this.editFieldValues?.user_id;
    this.preLeadStatus = this.editFieldValues?.lead_status ?? '';
    this.prePipelineId = this.editFieldValues?.pipeline_id;
    this.preLeadType = this.editFieldValues?.lead_type ?? '';
    this.preLeadSource = this.editFieldValues?.lead_source ?? '';
  }

  private pipelineLabel(pipelineId: unknown): string {
    if (pipelineId == null || pipelineId === '') {
      return '—';
    }
    const p = this.pipelineData.find(
      (x: any) => String(x.pipeline_id) === String(pipelineId)
    );
    return p?.pipeline_name || String(pipelineId);
  }

  saveSidebar(): void {
    this.btnSaveSidebar = true;
    const payload = {
      lead_id: this.editFieldValues.lead_id,
      client_id: this.localStorage.getClientId(),
      center_id: this.localStorage.getCenterId(),
      user_id: this.editFieldValues.user_id,
      pipeline_id: this.editFieldValues.pipeline_id,
      lead_status: this.editFieldValues.lead_status,
      lead_type: this.editFieldValues.lead_type,
      lead_source: this.editFieldValues.lead_source,
    };
    this.leadApi.updateLead(payload).subscribe({
      next: () => {
        const logs: Observable<unknown>[] = [];
        if (this.preUserId != null && String(this.preUserId) !== String(this.editFieldValues.user_id)) {
          const owner = this.users.find((u) => u.user_id === this.editFieldValues.user_id);
          const label = owner
            ? `${owner.fname} ${owner.lname}`.trim()
            : this.editFieldValues.user_id;
          logs.push(
            this.leadActivity.recordLeadFieldChange(
              this.leadId,
              'Assigned To',
              this.preUserId,
              label
            )
          );
        }
        const statusChanged =
          String(this.preLeadStatus ?? '') !==
          String(this.editFieldValues.lead_status ?? '');
        if (statusChanged && this.editFieldValues.lead_status) {
          logs.push(
            this.leadActivity.recordLeadStatusChange(
              this.leadId,
              this.preLeadStatus,
              this.editFieldValues.lead_status
            )
          );
        }
        if (String(this.prePipelineId ?? '') !== String(this.editFieldValues.pipeline_id ?? '')) {
          logs.push(
            this.leadActivity.recordLeadFieldChange(
              this.leadId,
              'Lead Pipeline',
              this.pipelineLabel(this.prePipelineId),
              this.pipelineLabel(this.editFieldValues.pipeline_id)
            )
          );
        }
        if (String(this.preLeadType ?? '') !== String(this.editFieldValues.lead_type ?? '')) {
          logs.push(
            this.leadActivity.recordLeadFieldChange(
              this.leadId,
              'Lead Type',
              this.preLeadType || '—',
              this.editFieldValues.lead_type || '—'
            )
          );
        }
        if (String(this.preLeadSource ?? '') !== String(this.editFieldValues.lead_source ?? '')) {
          logs.push(
            this.leadActivity.recordLeadFieldChange(
              this.leadId,
              'Lead Source',
              this.preLeadSource || '—',
              this.editFieldValues.lead_source || '—'
            )
          );
        }
        const finishSave = () => {
          this.snapshotSidebarBaselines();
          this.toast.success('Lead updated');
          this.btnSaveSidebar = false;
          this.reloadLeadList.emit();
          this.refreshTimelineAfterLog();
        };
        if (logs.length) {
          forkJoin(logs).subscribe({ next: finishSave, error: finishSave });
        } else {
          finishSave();
        }
      },
      error: (err) => {
        this.toast.error(err?.error?.message || 'Update failed');
        this.btnSaveSidebar = false;
      },
    });
  }

  closeProfile(): void {
    this.close.emit();
  }
}
